import "leaflet/dist/leaflet.css";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import axios from "axios";

const zipGeoURL = "/geo/tl_2020_us_zcta520.geojson";

// The national ZCTA file is ~26MB — fetch and parse it once per session.
let cachedZipGeoPromise = null;
const loadZipGeo = () => {
  if (!cachedZipGeoPromise) {
    cachedZipGeoPromise = fetch(zipGeoURL).then((res) => {
      if (!res.ok) {
        cachedZipGeoPromise = null;
        throw new Error(`Failed to load ZIP boundaries: ${res.status}`);
      }
      return res.json();
    });
  }
  return cachedZipGeoPromise;
};

const STATE_CENTERS = {
  AL: [32.36, -86.79], AK: [61.22, -152.40], AZ: [33.77, -111.43],
  AR: [34.95, -92.37], CA: [36.12, -119.85], CO: [39.06, -105.31],
  CT: [41.60, -72.73], DE: [39.32, -75.53], FL: [27.77, -81.66],
  GA: [33.75, -83.64], HI: [21.09, -157.50], ID: [44.26, -114.74],
  IL: [40.34, -89.40], IN: [39.77, -86.16], IA: [42.01, -93.77],
  KS: [38.53, -96.73], KY: [37.67, -84.67], LA: [31.17, -91.83],
  ME: [44.69, -69.40], MD: [39.06, -76.79], MA: [42.24, -71.54],
  MI: [43.33, -84.55], MN: [45.73, -95.57], MS: [32.77, -89.67],
  MO: [38.46, -92.33], MT: [47.05, -110.36], NE: [41.49, -99.90],
  NV: [38.31, -117.06], NH: [43.45, -71.55], NJ: [40.06, -74.74],
  NM: [34.84, -106.25], NY: [42.95, -74.22], NC: [35.76, -79.02],
  ND: [47.40, -99.78], OH: [40.39, -82.76], OK: [35.47, -96.93],
  OR: [44.98, -122.07], PA: [40.27, -77.19], RI: [41.68, -71.51],
  SC: [33.84, -80.93], SD: [44.30, -99.90], TN: [35.75, -86.68],
  TX: [31.05, -97.56], UT: [40.11, -111.89], VT: [44.05, -72.71],
  VA: [37.77, -78.17], WA: [47.39, -121.19], WV: [38.47, -80.97],
  WI: [44.27, -89.62], WY: [42.75, -107.30],
};

const extractCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates)) {
    return [];
  }
  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    typeof coordinates[1] === "number"
  ) {
    return [coordinates];
  }
  return coordinates.flatMap(extractCoordinates);
};

function MapUpdater({ center, zoom }) {
  const map = useMap();
  const lat = center ? center[0] : null;
  const lng = center ? center[1] : null;
  useEffect(() => {
    if (lat == null) return;
    map.setView([lat, lng], zoom);
    // Depend on the view VALUES, not the array identity — parent re-renders
    // must never yank the map away from wherever the user panned/zoomed.
  }, [lat, lng, zoom, map]);
  return null;
}

export default function ReferrerZipHeatmap({
  selectedStates = [],
  selectedZipCodes = [],
  selectedProviderTypes = [],
  mode = "opportunity",
  focusLabel = "",
  onZipSelect,
}) {
  const [zipGeoData, setZipGeoData] = useState(null);
  const [marketData, setMarketData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([37.09, -95.71]);
  const [mapZoom, setMapZoom] = useState(4);
  const geoJsonRef = useRef(null);

  // Stable keys: the arrays get new identities on every parent render, so
  // effects must key off their contents or they refire constantly (which
  // used to reset the map view on every zip click / data refresh).
  const statesKey = selectedStates.join(",");
  const zipsKey = selectedZipCodes.map((zip) => String(zip).slice(0, 5)).join(",");
  const typesKey = selectedProviderTypes.join(",");
  const focusKey = statesKey + "|" + zipsKey;
  const stateFocusRef = useRef(null);
  const dataFocusRef = useRef(null);

  useEffect(() => {
    if (selectedZipCodes.length > 0 || stateFocusRef.current === focusKey) {
      return;
    }
    if (selectedStates.length > 0) {
      const centers = selectedStates.map((s) => STATE_CENTERS[s]).filter(Boolean);
      if (centers.length > 0) {
        const avgLat = centers.reduce((s, c) => s + c[0], 0) / centers.length;
        const avgLng = centers.reduce((s, c) => s + c[1], 0) / centers.length;
        stateFocusRef.current = focusKey;
        setMapCenter([avgLat, avgLng]);
        setMapZoom(centers.length === 1 ? 6 : 5);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (selectedStates.length === 0 && selectedZipCodes.length === 0) {
          setMarketData({});
          setLoading(false);
          return;
        }

        const token = localStorage.getItem("token");
        const marketRequests = [];
        if (selectedZipCodes.length > 0) {
          const params = { zip_codes: selectedZipCodes.join(",") };
          if (selectedProviderTypes.length > 0) {
            params.provider_types = selectedProviderTypes.join(",");
          }
          marketRequests.push(
            axios.get(`${process.env.REACT_APP_API}/market-opportunity`, {
              params,
              headers: { Authorization: `Bearer ${token}` },
            }).catch((err) => {
              console.warn("Failed to load market data for practice area:", err.message);
              return { data: [] };
            })
          );
        } else {
          selectedStates.forEach((state) => {
            const params = { state };
            if (selectedProviderTypes.length > 0) {
              params.provider_types = selectedProviderTypes.join(",");
            }
            marketRequests.push(
              axios.get(`${process.env.REACT_APP_API}/market-opportunity`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
              }).catch((err) => {
                console.warn(`Failed to load market data for ${state}:`, err.message);
                return { data: [] };
              })
            );
          });
        }

        const [geojson, ...marketResponses] = await Promise.all([
          loadZipGeo(),
          ...marketRequests,
        ]);

        const marketStats = {};
        marketResponses.forEach((response) => {
          if (Array.isArray(response.data)) {
            response.data.forEach((row) => {
              marketStats[row.zip_code] = row;
            });
          }
        });

        // Render only the polygons we have data (or an explicit selection)
        // for — drawing all 33k national ZCTAs froze the map and re-centered
        // it on the national centroid instead of the chosen state.
        const zipSet = new Set(selectedZipCodes.map((zip) => String(zip).slice(0, 5)));
        const dataZipSet = new Set(Object.keys(marketStats).map((zip) => String(zip).slice(0, 5)));
        const keepSet = zipSet.size > 0 ? zipSet : dataZipSet;
        const filteredGeojson = {
          ...geojson,
          features: keepSet.size > 0
            ? geojson.features.filter((feature) => keepSet.has(String(feature.properties.ZCTA5CE20)))
            : [],
        };

        const focusFeatures = filteredGeojson.features || [];
        // Only refocus when the user changed WHAT is selected — never when the
        // same selection merely reloads (filter tweaks, zip clicks, refetches).
        if (focusFeatures.length > 0 && dataFocusRef.current !== focusKey) {
          dataFocusRef.current = focusKey;
          const coords = focusFeatures
            .filter((feature) => feature.geometry && feature.geometry.coordinates)
            .flatMap((feature) => extractCoordinates(feature.geometry.coordinates));

          if (coords.length > 0) {
            const avgLon = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
            const avgLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
            setMapCenter([avgLat, avgLon]);
            if (focusFeatures.length <= 10) {
              setMapZoom(9);
            } else if (focusFeatures.length <= 40) {
              setMapZoom(8);
            } else if (focusFeatures.length <= 100) {
              setMapZoom(7);
            } else {
              setMapZoom(6);
            }
          }
        }

        setZipGeoData(filteredGeojson);
        setMarketData(marketStats);
      } catch (err) {
        console.error("Error loading market data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statesKey, zipsKey, typesKey]);

  const maxPrivateVolume = useMemo(
    () => Object.values(marketData).reduce((max, d) => Math.max(max, d.est_private_volume || 0), 0),
    [marketData]
  );

  const getColorForMarket = (zipCode) => {
    const data = marketData[zipCode];
    if (!data) return "rgba(200,200,200,0.08)";

    const toRgba = (r, g, b, a) => `rgba(${r},${g},${b},${(a / 255).toFixed(2)})`;

    switch (mode) {
      case "opportunity": {
        const score = data.opportunity_score || 0;
        if (score >= 100) return toRgba(255, 20, 147, 200);
        if (score >= 50) return toRgba(255, 69, 0, Math.min(200, 150 + (score - 50)));
        if (score >= 11) {
          const ratio = (score - 11) / 38;
          return toRgba(255, Math.floor(140 + ratio * 115), Math.floor(ratio * 50), 160);
        }
        if (score >= 1) return toRgba(255, 255, 100, Math.min(160, 80 + score * 8));
        return "rgba(200,200,200,0.24)";
      }
      case "activity": {
        const activity = data.total_activity || 0;
        return toRgba(220, 38, 38, Math.min(200, 50 + activity * 8));
      }
      case "penetration": {
        if (!data.referrer_count) return "rgba(200,200,200,0.15)";
        const penetration = data.penetration_rate || 0; // percent (0-100)
        if (penetration <= 0) return toRgba(220, 38, 38, 150);
        if (penetration < 10) return toRgba(249, 115, 22, 160);
        if (penetration < 25) return toRgba(245, 158, 11, 170);
        if (penetration < 50) return toRgba(132, 204, 22, 175);
        return toRgba(22, 163, 74, 185);
      }
      case "value": {
        // Market-wide: estimated private-pay patient volume across ALL
        // referrers in the ZIP (relative to the hottest ZIP in view), not
        // just the user's own referral history.
        const vol = data.est_private_volume || 0;
        if (!vol || !maxPrivateVolume) return "rgba(200,200,200,0.15)";
        const ratio = vol / maxPrivateVolume;
        if (ratio >= 0.6) return toRgba(22, 101, 52, 190);
        if (ratio >= 0.35) return toRgba(34, 197, 94, 180);
        if (ratio >= 0.15) return toRgba(132, 204, 22, 170);
        if (ratio >= 0.05) return toRgba(250, 204, 21, 160);
        return toRgba(249, 115, 22, 140);
      }
      default: {
        const count = data.referrer_count || 0;
        return toRgba(59, 130, 246, Math.min(200, 30 + count * 15));
      }
    }
  };

  const style = useMemo(() => {
    return (feature) => {
      const zip = feature.properties.ZCTA5CE20;
      return {
        fillColor: getColorForMarket(zip),
        weight: 1,
        color: "rgba(255,255,255,0.4)",
        fillOpacity: 1,
      };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketData, mode]);

  const onEachFeature = (feature, layer) => {
    const zip = feature.properties.ZCTA5CE20;
    const data = marketData[zip];
    if (data) {
      layer.bindTooltip(
        `ZIP ${zip}<br>Score: ${data.opportunity_score || 0}<br>Referrers: ${data.referrer_count || 0}` +
        `<br>Penetrance: ${data.penetration_rate || 0}% (${data.matched_referrers || 0} referring)` +
        (data.est_private_volume ? `<br>Est. private-pay volume: ${Number(data.est_private_volume).toLocaleString()}` : "")
      );
    }
    layer.on("click", () => {
      if (onZipSelect) {
        onZipSelect(data ? { zip, ...data } : {
          zip, referrer_count: 0, patient_count: 0, matched_referrers: 0,
          untapped_referrers: 0, penetration_rate: 0, opportunity_score: 0,
          avg_activity: 0, market_category: "no_data",
        });
      }
    });
  };

  if (selectedStates.length === 0 && selectedZipCodes.length === 0) {
    return (
      <div className="p-8 bg-blue-50 border border-blue-200 rounded text-center">
        <p className="text-blue-800 font-medium text-lg mb-2">👆 Select a State to Begin</p>
        <p className="text-blue-600 text-sm">
          Choose one or more states from the dropdown above or apply a practice area to view market opportunity data.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 bg-gray-50 border rounded">
        <p className="text-gray-600">Loading market analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-700">Error: {error}</p>
      </div>
    );
  }

  const noData = Object.keys(marketData).length === 0;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Empty-result overlay: a filter that matches nothing must say so,
          not silently blank the map. */}
      {noData && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div className="px-5 py-4 bg-white/95 rounded-lg shadow-lg border border-amber-300 text-center" style={{ maxWidth: "420px", pointerEvents: "auto" }}>
            <p className="font-semibold text-gray-800 mb-1">No providers match this filter here</p>
            <p className="text-sm text-gray-600">
              {selectedProviderTypes.length > 0
                ? "None of the selected provider types practice in this area. Remove some specialty filters (or hit Clear) to see the full market."
                : "No provider data found for this selection."}
            </p>
          </div>
        </div>
      )}
      {/* Legend Overlay */}
      <div style={{ position: "absolute", top: "10px", right: "10px", maxWidth: "440px", zIndex: 1000, pointerEvents: "none" }}>
        <div className="p-3 bg-white/95 rounded-lg shadow-lg border border-gray-200" style={{ pointerEvents: "auto" }}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h4 className="font-medium text-gray-700 text-sm">
              {focusLabel || selectedStates.join(", ")} — {(zipGeoData?.features?.length || Object.keys(marketData).length).toLocaleString()} ZIP codes with referrers
            </h4>
            {selectedProviderTypes.length > 0 && (
              <span className="text-xs text-blue-600 font-medium">🔍 Filtered by specialty</span>
            )}
          </div>

          {mode === "opportunity" && (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(255, 20, 147)" }}></div>
                <span className="font-medium">Best (100+)</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(255, 69, 0)" }}></div>
                Good (50-99)
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(255, 200, 50)" }}></div>
                OK (11-49)
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(255, 255, 100)" }}></div>
                Poor (1-10)
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-3 bg-gray-300 rounded"></div>
                Minimal
              </span>
            </div>
          )}
          {mode === "penetration" && (
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(220, 38, 38)" }}></div><span className="font-medium">Untouched (0%)</span></span>
              <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(249, 115, 22)" }}></div>Early (&lt;10%)</span>
              <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(245, 158, 11)" }}></div>Developing (10-24%)</span>
              <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(132, 204, 22)" }}></div>Growing (25-49%)</span>
              <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(22, 163, 74)" }}></div>Established (50%+)</span>
              <span className="flex items-center gap-1"><div className="w-4 h-3 bg-gray-300 rounded"></div>No referrers</span>
            </div>
          )}
          {mode === "value" && (
            <div>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(22, 101, 52)" }}></div><span className="font-medium">Top (60%+ of max)</span></span>
                <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(34, 197, 94)" }}></div>High (35-59%)</span>
                <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(132, 204, 22)" }}></div>Medium (15-34%)</span>
                <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(250, 204, 21)" }}></div>Low (5-14%)</span>
                <span className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: "rgb(249, 115, 22)" }}></div>Minimal</span>
                <span className="flex items-center gap-1"><div className="w-4 h-3 bg-gray-300 rounded"></div>No data</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Estimated private-pay patient volume across all referrers in each ZIP, relative to the busiest ZIP in view.</p>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">💡 Click ZIP codes for details</p>
        </div>
      </div>

      <MapContainer center={mapCenter} zoom={mapZoom} style={{ width: "100%", height: "100%" }} scrollWheelZoom={true} preferCanvas={true}>
        <MapUpdater center={mapCenter} zoom={mapZoom} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          crossOrigin="anonymous"
        />
        {zipGeoData && (
          <GeoJSON
            key={`${mode}-${Object.keys(marketData).length}-${selectedZipCodes.length}`}
            ref={geoJsonRef}
            data={zipGeoData}
            style={style}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>
    </div>
  );
}
